import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronDown, Send, Loader2 } from 'lucide-react';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from './ui/accordion';
import { faqs, awards } from '../mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function FAQSection() {
  return (
    <section id="faq" className="py-20 bg-spin-sand">
      <div className="max-w-3xl mx-auto px-5">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-heading font-extrabold text-spin-dark">
            Häufige Fragen
          </h2>
          <hr className="section-divider" />
        </div>

        <Accordion type="single" collapsible className="w-full space-y-2">
          {faqs.map((f, i) => (
            <AccordionItem
              key={i}
              value={`item-${i}`}
              className="bg-white rounded-md border border-border data-[state=open]:border-spin-green data-[state=open]:shadow-md transition-all"
            >
              <AccordionTrigger className="px-5 py-4 text-left font-semibold text-spin-dark hover:no-underline">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="px-5 pb-5 text-spin-grey leading-relaxed">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

export function NewsletterSection() {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [accepted, setAccepted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!accepted) {
      toast.error('Bitte akzeptiere die Datenschutzbestimmungen.');
      return;
    }
    if (!email || !email.includes('@')) {
      toast.error('Bitte gib eine gültige E-Mail-Adresse ein.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email, name });
      toast.success('Danke! Du erhältst gleich eine Bestätigung.');
      setEmail('');
      setName('');
      setAccepted(false);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Etwas ist schiefgelaufen. Bitte versuche es erneut.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-spin-green relative overflow-hidden">
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-white/40 blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-spin-dark/40 blur-3xl" />
      </div>

      <div className="relative max-w-3xl mx-auto px-5 text-center text-white">
        <h2 className="text-3xl md:text-4xl font-heading font-extrabold">
          Bleib' auf dem Laufenden
        </h2>
        <hr className="section-divider light" />
        <p className="text-white/90 leading-relaxed max-w-2xl mx-auto mb-8">
          Um auf dem Laufenden zu bleiben, melde dich doch bei unserem Newsletter an, folge uns bei
          Facebook oder Instagram. Hier erfährst du auf jeden Fall als erstes, wenn es etwas Neues
          gibt. Über Feedback via E-Mail freuen wir uns auch immer.
        </p>

        <form onSubmit={submit} className="max-w-xl mx-auto">
          <div className="grid sm:grid-cols-2 gap-3 mb-3">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Dein Name (optional)"
              className="px-4 py-3 rounded-md text-spin-dark placeholder:text-spin-grey focus:outline-none focus:ring-2 focus:ring-white"
            />
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="deine@email.de"
              className="px-4 py-3 rounded-md text-spin-dark placeholder:text-spin-grey focus:outline-none focus:ring-2 focus:ring-white"
            />
          </div>

          <label className="flex items-start gap-2 text-sm text-white/90 text-left mb-4">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              className="mt-1 accent-white"
            />
            <span>
              Mit der Anmeldung akzeptiere ich die{' '}
              <a href="/datenschutz" className="underline hover:no-underline">
                Datenschutzbestimmungen
              </a>
              {' '}und abonniere den Newsletter von Spinsafe. Die Abmeldung ist jederzeit möglich.
            </span>
          </label>

          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center gap-2 px-7 py-3 bg-white text-spin-green font-bold rounded-md hover:bg-spin-sand transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
            {loading ? 'Wird gesendet...' : 'Newsletter abonnieren'}
          </button>
        </form>

        <p className="mt-8 text-white/80 text-sm">
          Oder schreib uns direkt eine E-Mail an{' '}
          <a href="mailto:info@spinsafe.de" className="underline font-semibold">
            info@spinsafe.de
          </a>
        </p>
      </div>
    </section>
  );
}

export function TeamSection() {
  return (
    <section id="team" className="py-20 bg-white">
      <div className="max-w-3xl mx-auto px-5">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-heading font-extrabold text-spin-dark">
            Das Team hinter Spinsafe
          </h2>
          <hr className="section-divider" />
        </div>
        <p className="text-spin-grey leading-relaxed text-lg text-center">
          Jeden Sommer häufen sich Berichte von Smartphone- und Wertsachendiebstählen auf
          Liegewiesen, aber auch auf Festivals. Dies wollten wir selbst einfach nicht mehr hinnehmen
          und haben uns vorgenommen, einen umfassenden und mobilen Schutz vor Gelegenheitsdiebstahl
          für das teure Hab und Gut zu entwickeln. Das Ergebnis ist der{' '}
          <strong className="text-spin-dark">Spinsafe Outdoor-Tresor</strong>. Unsere Vision ist, dass
          die Menschen ihre Freizeit auf der Liegewiese – sei es am See, im Park, im Freibad oder
          beim Festival – unbeschwert genießen können, ohne ständig einen Kontrollblick zu ihren
          Wertsachen schweifen lassen zu müssen. Mit unserem Handeln verfolgen wir einen
          nachhaltigen Ansatz mit deutscher Fertigung und regionalen Partnern. Zusammen arbeiten
          wir daran unser Produkt stets zu optimieren, indem wir auch die Features unserer
          Smartphone Alarm-App regelmäßig erweitern.
        </p>
      </div>
    </section>
  );
}

export function AwardsSection() {
  return (
    <section id="awards" className="py-20 bg-spin-sand">
      <div className="max-w-6xl mx-auto px-5">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-heading font-extrabold text-spin-dark">
            Auszeichnungen
          </h2>
          <hr className="section-divider" />
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {awards.map((a, i) => (
            <div key={i} className="text-center bg-white rounded-lg p-7 shadow-sm hover:shadow-md transition-shadow">
              <div className="h-24 flex items-center justify-center mb-4">
                <img src={a.img} alt={a.alt} className="max-h-24 object-contain" />
              </div>
              <hr className="section-divider" />
              <p className="text-spin-grey text-sm leading-relaxed">{a.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
